/**
 * BlockDateModal — B-03 (SCRUM-1145), B-04 (SCRUM-1146), B-NEW-04 (SCRUM-1151)
 *
 * Modal for blocking dates on the guide availability calendar.
 * Two options:
 *   1. "Just this date" (default) — block a single date or date range
 *   2. "Repeat this block" — frequency selector + pattern pickers
 *
 * Just-this-date path (B-NEW-04):
 *   Calls blockDates({ startDate, endDate, reason }) for both single and range.
 *   blockDates POST /api/v1/availability/:guideId/block (creates group record first).
 *
 * Repeat path:
 *   Weekly (B-04): day-of-week checkbox grid → daysToRRule() → POST recurring-blocks
 *   Monthly (B-05): ordinal position + day-of-week dropdowns → generateMonthlyOrdinalRRule()
 *   Yearly (B-06): FREQ=YEARLY;BYMONTH=N;BYMONTHDAY=D from selectedDate
 *
 * After successful block: closes modal + fires onBlockSuccess() callback.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {string} props.selectedDate - YYYY-MM-DD date the guide clicked
 * @param {Function} props.onClose - Called on Cancel or Escape
 * @param {Function} props.onBlockSuccess - Called after successful block
 * @param {Function} props.blockDates - ({ startDate, endDate, reason? }) => Promise — for just-this-date path (B-NEW-04)
 * @param {Function} props.toggleDate - (dateStr, available) => Promise — kept for other contexts, not called from just-this-date path
 * @param {Function} props.batchBlockDates - kept for backward compatibility, not called from just-this-date path
 * @param {Function} props.createBlock - (blockData) => Promise — for recurring blocks
 * @param {boolean} [props.editMode=false] - When true, modal is in edit mode (B-NEW-06 SCRUM-1153)
 * @param {Object|null} [props.blockGroup] - Block group being edited: { id, start_date, end_date, label }
 * @param {Function} [props.onEditBlock] - ({ startDate, endDate, label }) => Promise — for edit mode
 */

import React, { useState, useEffect, useCallback } from 'react';
import { daysToRRule, getOrdinalFromDate, generateMonthlyOrdinalRRule, rruleToHumanReadable } from './utils/rruleHelpers';

// Day constants
const DAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const INDEX_TO_ABBREV = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

// Monthly ordinal label maps
const ORDINAL_LABELS = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', '-1': 'last' };
const DAYCODE_LABELS = {
  SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
  TH: 'Thursday', FR: 'Friday', SA: 'Saturday',
};

/** Extract JS day-of-week index (0=Su..6=Sa) from a YYYY-MM-DD string. */
const dayOfWeekFromDate = (dateStr) => {
  if (!dateStr) { return 0; }
  return new Date(dateStr + 'T12:00:00').getDay();
};

/** Format a YYYY-MM-DD string as "Month D" for the yearly label. */
const formatYearlyLabel = (dateStr) => {
  if (!dateStr) { return ''; }
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

/** Build a yearly RRULE from a YYYY-MM-DD string. */
const buildYearlyRRule = (dateStr) => {
  if (!dateStr) { return ''; }
  const d = new Date(dateStr + 'T12:00:00');
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return `FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${day}`;
};

/** Join day names with commas and "and" for the summary. */
const daysToLabel = (days) => {
  const sorted = [...days].sort((a, b) => a - b);
  const names = sorted.map(d => DAY_LABELS[d]);
  if (names.length === 0) { return ''; }
  if (names.length === 1) { return names[0]; }
  if (names.length === 2) { return `${names[0]} and ${names[1]}`; }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

export default function BlockDateModal({
  isOpen,
  selectedDate,
  onClose,
  onBlockSuccess,
  blockDates,
  toggleDate: _toggleDate,
  batchBlockDates: _batchBlockDates,
  createBlock,
  editMode = false,
  blockGroup = null,
  onEditBlock = null,
}) {
  const [selectedOption, setSelectedOption] = useState('just-this-date');
  const [startDate, setStartDate] = useState(selectedDate);
  const [endDate, setEndDate] = useState(selectedDate);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [blockError, setBlockError] = useState(null);
  const [frequency, setFrequency] = useState('Weekly');
  const [selectedDays, setSelectedDays] = useState([]);
  const [monthlyOrdinal, setMonthlyOrdinal] = useState(() => getOrdinalFromDate(selectedDate));

  // Reset state when modal opens with a new date
  useEffect(() => {
    if (isOpen) {
      if (editMode && blockGroup) {
        setSelectedOption('just-this-date');
        setStartDate(blockGroup.start_date || blockGroup.startDate || selectedDate);
        setEndDate(blockGroup.end_date || blockGroup.endDate || selectedDate);
        setReason(blockGroup.label || '');
      } else {
        setSelectedOption('just-this-date');
        setStartDate(selectedDate);
        setEndDate(selectedDate);
        setReason('');
      }
      setFrequency('Weekly');
      setSelectedDays([dayOfWeekFromDate(selectedDate)]);
      setMonthlyOrdinal(getOrdinalFromDate(selectedDate));
      setSubmitting(false);
      setBlockError(null);
    }
  }, [isOpen, selectedDate, editMode, blockGroup]);

  // Escape key closes modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const toggleDay = (idx) => {
    setSelectedDays(prev =>
      prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx]
    );
  };

  const effectiveStartDate = editMode ? startDate : selectedDate;
  const endDateError = endDate < effectiveStartDate
    ? 'End date must be on or after start date'
    : null;

  const isSubmitDisabled =
    submitting ||
    (selectedOption === 'just-this-date' && !!endDateError) ||
    (selectedOption === 'repeat' && frequency === 'Weekly' && selectedDays.length === 0);

  const handleSubmit = useCallback(async () => {
    if (isSubmitDisabled) {
      return;
    }

    setSubmitting(true);
    setBlockError(null);
    try {
      if (editMode && blockGroup && onEditBlock) {
        // B-NEW-06 (SCRUM-1153): edit mode — call PUT endpoint
        await onEditBlock({
          startDate: startDate,
          endDate,
          label: reason || undefined,
        });
        onClose();
        if (onBlockSuccess) {
          onBlockSuccess();
        }
        return;
      }

      if (selectedOption === 'just-this-date') {
        // B-NEW-04 (SCRUM-1151): both single-date and range use the group-first endpoint
        await blockDates({ startDate: selectedDate, endDate });
      } else if (selectedOption === 'repeat' && frequency === 'Weekly') {
        const abbrevs = [...selectedDays]
          .sort((a, b) => a - b)
          .map(d => INDEX_TO_ABBREV[d]);
        const rrule = daysToRRule(abbrevs);
        await createBlock({
          block_name: `Weekly block – ${daysToLabel(selectedDays)}`,
          recurrence_rule: rrule,
          start_date: selectedDate,
        });
      } else if (selectedOption === 'repeat' && frequency === 'Monthly') {
        const rrule = generateMonthlyOrdinalRRule(monthlyOrdinal.dayCode, monthlyOrdinal.position);
        const label = `${ORDINAL_LABELS[monthlyOrdinal.position]} ${DAYCODE_LABELS[monthlyOrdinal.dayCode]}`;
        await createBlock({
          block_name: `Monthly block – ${label}`,
          recurrence_rule: rrule,
          start_date: selectedDate,
        });
      } else if (selectedOption === 'repeat' && frequency === 'Yearly') {
        const rrule = buildYearlyRRule(selectedDate);
        await createBlock({
          block_name: `Yearly block – ${formatYearlyLabel(selectedDate)}`,
          recurrence_rule: rrule,
          start_date: selectedDate,
        });
      }
      onClose();
      if (onBlockSuccess) {
        onBlockSuccess();
      }
    } catch (err) {
      if (err && err.status === 409) {
        setBlockError('Some dates have confirmed bookings and cannot be blocked.');
      } else {
        setBlockError('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  }, [isSubmitDisabled, selectedOption, selectedDate, startDate, endDate, reason, frequency, selectedDays, monthlyOrdinal, blockDates, createBlock, onClose, onBlockSuccess, editMode, blockGroup, onEditBlock]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      data-testid="block-date-modal-backdrop"
    >
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full" data-testid="block-date-modal">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-testid="modal-title">
          {editMode ? 'Edit block' : 'Block dates'}
        </h2>

        {/* Option selector — hidden in edit mode (always just-this-date) */}
        {!editMode && (
          <div className="flex flex-col gap-2 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="block-option"
                value="just-this-date"
                checked={selectedOption === 'just-this-date'}
                onChange={() => setSelectedOption('just-this-date')}
                className="text-brand-primary focus:ring-brand-primary"
                data-testid="option-just-this-date"
              />
              <span className="text-sm font-medium text-gray-900">Just this date</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="block-option"
                value="repeat"
                checked={selectedOption === 'repeat'}
                onChange={() => setSelectedOption('repeat')}
                className="text-brand-primary focus:ring-brand-primary"
                data-testid="option-repeat"
              />
              <span className="text-sm font-medium text-gray-900">Repeat this block</span>
            </label>
          </div>
        )}

        {/* Just this date form (also used in edit mode) */}
        {(selectedOption === 'just-this-date' || editMode) && (
          <div className="flex flex-col gap-3 mb-4" data-testid="just-this-date-section">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
              <input
                type="date"
                value={editMode ? startDate : selectedDate}
                readOnly={!editMode}
                onChange={editMode ? (e) => setStartDate(e.target.value) : undefined}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 ${!editMode ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
              <input
                type="date"
                value={endDate}
                min={editMode ? startDate : selectedDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                data-testid="end-date-input"
              />
            </div>
            {endDateError && (
              <p className="text-sm text-red-600" data-testid="end-date-error">{endDateError}</p>
            )}
            {/* Reason/label field (B-NEW-06 SCRUM-1153) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Vacation, Family event"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                data-testid="reason-input"
              />
            </div>
          </div>
        )}

        {/* Repeat this block — frequency selector + pattern pickers */}
        {selectedOption === 'repeat' && (
          <div className="flex flex-col gap-3 mb-4" data-testid="repeat-section">
            {/* Frequency selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
              <div className="flex gap-2" data-testid="frequency-selector">
                {['Weekly', 'Monthly', 'Yearly'].map(freq => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setFrequency(freq)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      frequency === freq
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-brand-primary'
                    }`}
                    data-testid={`freq-${freq.toLowerCase()}`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
            </div>

            {/* Weekly picker */}
            {frequency === 'Weekly' && (
              <div data-testid="weekly-picker">
                <label className="block text-sm font-medium text-gray-700 mb-2">Repeat on</label>
                <div className="flex gap-1">
                  {DAY_SHORT.map((day, idx) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`w-9 h-9 rounded-full text-xs font-medium transition-colors ${
                        selectedDays.includes(idx)
                          ? 'bg-brand-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`day-${day.toLowerCase()}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                {selectedDays.length === 0 && (
                  <p className="text-xs text-red-600 mt-1" data-testid="weekly-days-error">
                    Select at least one day
                  </p>
                )}
              </div>
            )}

            {/* Monthly ordinal picker — B-05 (SCRUM-1147) */}
            {frequency === 'Monthly' && (
              <div data-testid="monthly-picker">
                <label className="block text-sm font-medium text-gray-700 mb-2">Repeat on the</label>
                <div className="flex gap-2 items-center">
                  <select
                    value={monthlyOrdinal.position}
                    onChange={(e) => setMonthlyOrdinal(prev => ({
                      ...prev,
                      position: parseInt(e.target.value, 10),
                    }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    data-testid="monthly-ordinal-select"
                  >
                    <option value={1}>First</option>
                    <option value={2}>Second</option>
                    <option value={3}>Third</option>
                    <option value={4}>Fourth</option>
                    <option value={-1}>Last</option>
                  </select>
                  <select
                    value={monthlyOrdinal.dayCode}
                    onChange={(e) => setMonthlyOrdinal(prev => ({
                      ...prev,
                      dayCode: e.target.value,
                    }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    data-testid="monthly-day-select"
                  >
                    <option value="SU">Sunday</option>
                    <option value="MO">Monday</option>
                    <option value="TU">Tuesday</option>
                    <option value="WE">Wednesday</option>
                    <option value="TH">Thursday</option>
                    <option value="FR">Friday</option>
                    <option value="SA">Saturday</option>
                  </select>
                </div>
              </div>
            )}

            {/* Yearly — B-06 */}
            {frequency === 'Yearly' && (
              <div data-testid="yearly-section">
                <p className="text-sm text-gray-600">
                  This block will repeat every year on{' '}
                  <span className="font-medium">{formatYearlyLabel(selectedDate)}</span>.
                </p>
              </div>
            )}

            {/* Summary — live rruleToHumanReadable() (B-08, SCRUM-1150) */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3 text-xs text-gray-600" data-testid="repeat-summary">
              {(() => {
                let rrule = null;
                if (frequency === 'Weekly' && selectedDays.length > 0) {
                  const abbrevs = [...selectedDays]
                    .sort((a, b) => a - b)
                    .map(d => INDEX_TO_ABBREV[d]);
                  rrule = daysToRRule(abbrevs);
                } else if (frequency === 'Monthly') {
                  rrule = generateMonthlyOrdinalRRule(monthlyOrdinal.dayCode, monthlyOrdinal.position);
                } else if (frequency === 'Yearly') {
                  rrule = buildYearlyRRule(selectedDate);
                }
                if (!rrule) { return 'Select days to see the recurrence summary'; }
                const readable = rruleToHumanReadable(rrule);
                return `This date will be blocked ${readable.charAt(0).toLowerCase() + readable.slice(1)}`;
              })()}
            </div>
          </div>
        )}

        {/* Inline error */}
        {blockError && (
          <p className="text-sm text-red-600 mb-3" data-testid="block-error">{blockError}</p>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            data-testid="block-button"
          >
            {submitting ? (editMode ? 'Saving…' : 'Blocking…') : (editMode ? 'Save changes' : 'Block dates')}
          </button>
        </div>
      </div>
    </div>
  );
}
